import React from 'react';

export const FieldDiagram = ({ showEndZones = true, children, className = "" }) => {
  const yardLines = [10, 20, 30, 40, 50, 40, 30, 20, 10];
  
  return (
    <div 
      className={`relative w-full aspect-[2/1] md:aspect-[3/1] bg-emerald-900 rounded-lg overflow-hidden ${className}`}
      style={{
        background: 'linear-gradient(to right, #064e3b 0%, #065f46 50%, #064e3b 100%)'
      }}
      data-testid="field-diagram"
    >
      {/* End zones */}
      {showEndZones && (
        <>
          <div 
            className="absolute left-0 top-0 bottom-0 w-[10%] bg-emerald-800/50 flex items-center justify-center"
            style={{ borderRight: '4px solid rgba(255,255,255,0.3)' }}
          >
            <span 
              className="text-emerald-400 font-bold text-xs md:text-sm uppercase tracking-widest"
              style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
            >
              End Zone
            </span>
          </div>
          <div 
            className="absolute right-0 top-0 bottom-0 w-[10%] bg-orange-900/30 flex items-center justify-center"
            style={{ borderLeft: '4px solid rgba(255,255,255,0.3)' }}
          >
            <span 
              className="text-orange-400 font-bold text-xs md:text-sm uppercase tracking-widest"
              style={{ writingMode: 'vertical-lr' }}
            >
              End Zone
            </span>
          </div>
        </>
      )}
      
      {/* Yard lines */}
      <div className="absolute left-[10%] right-[10%] top-0 bottom-0">
        {yardLines.map((yard, index) => (
          <React.Fragment key={index}>
            <div 
              className="absolute top-0 bottom-0 w-[2px] bg-white/30"
              style={{ left: `${(index + 1) * 10}%` }}
            />
            <span 
              className="absolute text-white/40 font-bold text-[10px] md:text-sm"
              style={{ 
                left: `${(index + 1) * 10}%`, 
                top: '8%',
                transform: 'translateX(-50%)',
                fontFamily: 'Barlow Condensed, sans-serif'
              }}
            >
              {yard}
            </span>
            <span 
              className="absolute text-white/40 font-bold text-[10px] md:text-sm"
              style={{ 
                left: `${(index + 1) * 10}%`, 
                bottom: '8%',
                transform: 'translateX(-50%) rotate(180deg)',
                fontFamily: 'Barlow Condensed, sans-serif'
              }}
            >
              {yard}
            </span>
          </React.Fragment>
        ))}
        
        {/* Center line (50 yard) */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white/50"
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        />
        
        {/* Hash marks */}
        {Array.from({ length: 99 }).map((_, i) => (
          <React.Fragment key={`hash-${i}`}>
            <div 
              className="absolute w-[2px] h-[4px] bg-white/20"
              style={{ left: `${(i + 1)}%`, top: '30%' }}
            />
            <div 
              className="absolute w-[2px] h-[4px] bg-white/20"
              style={{ left: `${(i + 1)}%`, bottom: '30%' }}
            />
          </React.Fragment>
        ))}
      </div>
      
      {/* Pylon indicators */}
      <div className="absolute left-[10%] top-0 w-3 h-3 bg-orange-500 rounded-sm" />
      <div className="absolute left-[10%] bottom-0 w-3 h-3 bg-orange-500 rounded-sm" />
      <div className="absolute right-[10%] top-0 w-3 h-3 bg-orange-500 rounded-sm" />
      <div className="absolute right-[10%] bottom-0 w-3 h-3 bg-orange-500 rounded-sm" />
      
      {/* Children (for player positions) */}
      {children}
    </div>
  );
};

export const PlayerDot = ({ 
  x, 
  y, 
  position, 
  isQB = false, 
  isDef = false,
  color,
  showLabel = true 
}) => {
  const bgColor = color || (isQB ? '#eab308' : isDef ? '#ef4444' : '#3b82f6');
  
  return (
    <div 
      className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <div 
        className="w-4 h-4 md:w-5 md:h-5 rounded-full border-2 border-white transition-transform duration-200 group-hover:scale-125"
        style={{ 
          backgroundColor: bgColor,
          boxShadow: `0 0 8px ${bgColor}`
        }}
      />
      {showLabel && (
        <span 
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[8px] md:text-xs font-bold text-white/70 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {position}
        </span>
      )}
    </div>
  );
};

export const FormationDiagram = ({ formation, title, description }) => {
  return (
    <div className="bg-slate-800/50 rounded-xl p-4 md:p-6 border border-slate-700">
      <h4 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {title}
      </h4>
      <p className="text-slate-400 text-sm mb-4">{description}</p>
      <FieldDiagram showEndZones={false} className="rounded-lg">
        {formation.players.map((player, index) => (
          <PlayerDot 
            key={index}
            x={player.x}
            y={player.y}
            position={player.position}
            isQB={player.isQB}
            isDef={player.isDef}
            color={player.color}
          />
        ))}
      </FieldDiagram>
    </div>
  );
};

export default FieldDiagram;
